/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 9/6/2023
 */

import {
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Paper,
  Typography,
  FormHelperText
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useLoginMutation } from "../../Store/Services/AuthService";
import 'react-toastify/dist/ReactToastify.css';
import styles from "./LoginForm.module.css";
import { login } from "../../Store/Slices/authSlice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
// import { useLoginMutation } from "../../Store/Services/AuthService";


const schema = object({
  username: string().trim().email('Please provide a valid email').required("Please fill out your email"),
  password: string().required("Password is required"),
}).required();

const LoginForm = ({ onClose, onCloseFinal }) => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [triggerLogin, { isLoading }] = useLoginMutation();

  const handleClickShowPassword = () => {
    setShowPassword((currentShowState) => !currentShowState);
  };

  const onSubmit = async (data) => {
    try {
      const { username, roles, access_token } = await triggerLogin(data).unwrap();
      dispatch(login({ access_token, details: { username, roles } }));
      if (!window.location.pathname.startsWith('/checkout/')) {
        window.location.reload();
      }
    } catch (err) {
   
      const errorMessage = err?.status === 400
      ? "Invalid username or password"
      : err?.data?.message || "An unexpected error occurred";
      // const message = (err.status === 400) ? "Invalid Username or Password" : "An unexpected error occurred";
      toast.error( err.data?.message , {
        position: "bottom-right",
        hideProgressBar: true,
        closeOnClick: true,
      })
    }
    onClose();
    onCloseFinal()
  };

  return (
    <Container>
      {/* <Typography className={styles.formTopic}>LOGIN</Typography> */}
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className={styles.formItem}>
          <TextField
            label="Email"
            className={styles.formText}
            {...register("username")}
            size="small"
            fullWidth
            error={!!errors.username}
          />
          {errors.username && <FormHelperText error> {errors.username?.message} </FormHelperText> }
        </div>
        <div className={styles.formItem}>
          <TextField
            label="Password"
            className={styles.formText}
            {...register("password")}
            size="small"
            fullWidth
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment:(
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} edge='end'>
                    {showPassword ?  <VisibilityOff /> : <Visibility /> }
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={!!errors.password}
          />
          {errors.password && <FormHelperText error> {errors.password?.message} </FormHelperText>  }
        </div>
        <div>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={styles.submitButton}
            disabled={isLoading}
          >
            Login
          </Button>
        </div>
      </form>
    </Container>
  );
};

export default LoginForm;
